/**
 * /fuel — FUEL by SAA public ordering page.
 * Public checkout · calendar picker · dietary fields · Stripe redirect.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Utensils, Droplet, Zap, Apple, GlassWater, CalendarDays, AlertTriangle, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const isWeekend = (d) => { const w = d.getDay(); return w === 0 || w === 6; };
const fmt = (d) => d.toISOString().slice(0, 10);

// Cutoff: orders for date D must be placed before 5PM on D-1.
const isPastCutoff = (d) => {
  const cutoff = new Date(d); cutoff.setDate(cutoff.getDate() - 1); cutoff.setHours(17, 0, 0, 0);
  return new Date() > cutoff;
};

const FuelLanding = () => {
  const [cfg, setCfg] = useState(null);
  const [view, setView] = useState('week');   // 'week' | 'month'
  const [anchor, setAnchor] = useState(new Date());
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState({ student_name: '', parent_name: '', parent_email: '', phone: '', allergies: '', dislikes: '' });
  const [noAllergies, setNoAllergies] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/fuel/config').then((r) => setCfg(r.data)).catch(() => toast.error('Could not load menu')); }, []);

  const days = useMemo(() => {
    const arr = [];
    if (view === 'week') {
      const start = new Date(anchor); start.setDate(start.getDate() - start.getDay() + 1);   // Monday
      for (let i = 0; i < 5; i++) { const d = new Date(start); d.setDate(start.getDate() + i); arr.push(d); }
    } else {
      const y = anchor.getFullYear(); const m = anchor.getMonth();
      const last = new Date(y, m + 1, 0).getDate();
      for (let i = 1; i <= last; i++) { const d = new Date(y, m, i); if (!isWeekend(d)) arr.push(d); }
    }
    return arr;
  }, [view, anchor]);

  const toggleDay = (d) => {
    if (isPastCutoff(d)) return;
    const s = fmt(d);
    const next = new Set(selected);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSelected(next);
  };

  const daysCount = selected.size;
  const subtotal  = +(daysCount * (cfg?.base_daily_rate || 20)).toFixed(2);
  const discount  = daysCount >= (cfg?.multi_day_threshold || 10) ? +(subtotal * (cfg?.multi_day_discount_pct || 0.1)).toFixed(2) : 0;
  const total     = +(subtotal - discount).toFixed(2);

  const canCheckout = daysCount > 0 && form.student_name && form.parent_name && form.parent_email && form.phone
    && (noAllergies || form.allergies.trim());

  const checkout = async () => {
    setBusy(true);
    try {
      const payload = {
        dates: Array.from(selected).sort(),
        student_name: form.student_name.trim(),
        parent_name: form.parent_name.trim(),
        parent_email: form.parent_email.trim(),
        phone: form.phone.trim(),
        allergies: noAllergies ? 'None' : form.allergies.trim(),
        dislikes: form.dislikes.trim() || null,
        origin_url: window.location.origin,
      };
      const r = await api.post('/fuel/checkout', payload);
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-14 pb-24 overflow-x-hidden" data-testid="fuel-landing">
      <div className="container mx-auto max-w-5xl px-4">
        {/* Hero */}
        <div className="border-b-2 border-zinc-900 pb-6 mb-8">
          <div className="text-[10px] tracking-[0.5em] uppercase font-black text-emerald-400">FUEL by SAA</div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">Fresh Unrefined Energy for <span className="text-emerald-400">Leaders</span></h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-2xl">{cfg.sub_slogan}</p>
        </div>

        {/* 5-Star Meal Prep · Sample menu with 5 categories */}
        <div className="mb-8" data-testid="fuel-menu">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-1">
            <div>
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white leading-none">5 Star Meal Prep</h2>
              <div className="text-[11px] tracking-[0.4em] uppercase font-black text-emerald-400 mt-2">Fuel Up</div>
            </div>
            <div className="border-2 border-emerald-500 bg-emerald-500/10 px-3 py-2 flex items-center gap-2" data-testid="five-star-badge">
              <div className="flex text-emerald-400">
                {[0,1,2,3,4].map((i) => <Star key={i} className="w-3 h-3 fill-current" />)}
              </div>
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400">5-Star Meal · $20</div>
                <div className="text-[9px] uppercase tracking-widest font-black text-white">Major Gains</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-400 mt-3 mb-4 max-w-2xl">Every day, every meal is a <span className="text-emerald-400 font-black">5-Star Meal</span> — one item from all five categories: Lunch · Juice · Hydration · Shake · Snack. Flat $20 · zero guesswork · maximum fuel.</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MenuCard icon={<Utensils className="w-5 h-5" />}   label="Lunch"     name={cfg.menu.lunch.name}     sub={cfg.menu.lunch.sides} />
            <MenuCard icon={<GlassWater className="w-5 h-5" />} label="Juice"     name={cfg.menu.juice.name}     sub={cfg.menu.juice.ingredients} />
            <MenuCard icon={<Droplet className="w-5 h-5" />}    label="Hydration" name={cfg.menu.hydration.name} sub="Electrolyte-infused" />
            <MenuCard icon={<Zap className="w-5 h-5" />}        label="Shake"     name={cfg.menu.shake.name}     sub={cfg.menu.shake.ingredients} />
            <MenuCard icon={<Apple className="w-5 h-5" />}      label="Snack"     name={cfg.menu.snack.name}     sub="Fresh whole-food" />
          </div>
        </div>

        {/* Calendar */}
        <div className="mb-8" data-testid="fuel-calendar">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Pick your dates · $20/day · Mon-Fri only · Cutoff 5PM prior day</div>
            <div className="flex gap-2">
              <button onClick={() => setView('week')} className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 ${view === 'week' ? 'bg-emerald-500 text-black border-emerald-500' : 'text-zinc-400 border-zinc-800'}`} data-testid="view-week">Week</button>
              <button onClick={() => setView('month')} className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 ${view === 'month' ? 'bg-emerald-500 text-black border-emerald-500' : 'text-zinc-400 border-zinc-800'}`} data-testid="view-month">Month</button>
            </div>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { const n = new Date(anchor); n.setDate(n.getDate() - (view === 'week' ? 7 : 30)); setAnchor(n); }} className="text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-zinc-800" data-testid="cal-prev">◄ Prev</button>
            <div className="text-sm font-black uppercase tracking-widest text-white flex-1 text-center"><CalendarDays className="w-4 h-4 inline mr-1" />{anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
            <button onClick={() => { const n = new Date(anchor); n.setDate(n.getDate() + (view === 'week' ? 7 : 30)); setAnchor(n); }} className="text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-zinc-800" data-testid="cal-next">Next ►</button>
          </div>
          <div className={`grid gap-2 ${view === 'week' ? 'grid-cols-5' : 'grid-cols-3 md:grid-cols-5 lg:grid-cols-7'}`}>
            {days.map((d) => {
              const s = fmt(d);
              const past = isPastCutoff(d);
              const isSel = selected.has(s);
              return (
                <button key={s} onClick={() => toggleDay(d)} disabled={past}
                  className={`border-2 p-3 text-left transition-all ${past ? 'border-zinc-900 bg-black text-zinc-700 cursor-not-allowed' : isSel ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300' : 'border-zinc-800 hover:border-emerald-500/50 text-white'}`}
                  data-testid={`day-${s}`}>
                  <div className="text-[9px] uppercase tracking-widest font-black opacity-70">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className="text-xl font-black">{d.getDate()}</div>
                  <div className="text-[9px] opacity-60">{past ? 'past cutoff' : isSel ? 'selected' : '$20'}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <div className="grid md:grid-cols-2 gap-3 mb-6" data-testid="fuel-form">
          <Input label="Student Full Name *" value={form.student_name} onChange={(v) => setForm({ ...form, student_name: v })} testId="fuel-student-name" />
          <Input label="Parent/Payer Full Name *" value={form.parent_name} onChange={(v) => setForm({ ...form, parent_name: v })} testId="fuel-parent-name" />
          <Input label="Parent Email *" type="email" value={form.parent_email} onChange={(v) => setForm({ ...form, parent_email: v })} testId="fuel-parent-email" />
          <Input label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="fuel-phone" />
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-red-400 mb-1">
              <AlertTriangle className="w-3 h-3" /> Food Allergies (required)
              <label className="ml-auto flex items-center gap-1 text-zinc-500 normal-case tracking-normal font-normal text-[11px]">
                <input type="checkbox" checked={noAllergies} onChange={(e) => setNoAllergies(e.target.checked)} data-testid="fuel-no-allergies" /> None
              </label>
            </label>
            {!noAllergies && (
              <input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                placeholder="e.g. peanut, dairy, shellfish"
                className="w-full bg-black border-2 border-red-500 px-3 py-2.5 text-sm font-black text-red-400 outline-none"
                style={{ color: '#FF0000' }}
                data-testid="fuel-allergies" />
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1">Yuck / No Thank You (optional)</label>
            <input value={form.dislikes} onChange={(e) => setForm({ ...form, dislikes: e.target.value })}
              placeholder="e.g. no mushrooms, prefer almond milk"
              className="w-full bg-black border-2 border-zinc-800 focus:border-orange-500 px-3 py-2.5 text-sm outline-none"
              data-testid="fuel-dislikes" />
          </div>
        </div>

        {/* Summary */}
        <div className="border-2 border-emerald-500 bg-emerald-500/[0.04] p-5 mb-4" data-testid="fuel-summary">
          <div className="text-[10px] uppercase tracking-widest font-black text-emerald-400 mb-2">Order Summary</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-zinc-400">Days selected</div><div className="text-right font-black">{daysCount}</div>
            <div className="text-zinc-400">Subtotal</div><div className="text-right font-black">${subtotal.toFixed(2)}</div>
            {discount > 0 && (<>
              <div className="text-emerald-400 font-black">10% Multi-Day Savings!</div>
              <div className="text-right text-emerald-400 font-black">– ${discount.toFixed(2)}</div>
            </>)}
            <div className="text-white font-black text-lg pt-2 border-t border-zinc-800 col-span-2 flex justify-between mt-2">
              <span>Total</span><span className="text-emerald-400">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <button onClick={checkout} disabled={busy || !canCheckout}
          className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-500 text-black py-4 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2"
          data-testid="fuel-checkout-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay $${total.toFixed(2)} · Secure Stripe Checkout`}
        </button>
      </div>
    </div>
  );
};

const MenuCard = ({ icon, label, name, sub }) => (
  <div className="border-2 border-zinc-900 bg-zinc-950 p-3 hover:border-emerald-500/50 transition-colors" data-testid={`menu-${label.toLowerCase()}`}>
    <div className="text-emerald-400 mb-1">{icon}</div>
    <div className="text-[9px] uppercase tracking-widest font-black text-emerald-400">{label}</div>
    <div className="text-[13px] font-black text-white mt-1 leading-tight">{name}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-1 leading-tight">{sub}</div>}
  </div>
);

const Input = ({ label, type = 'text', value, onChange, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-black border-2 border-zinc-800 focus:border-emerald-500 px-3 py-2.5 text-sm outline-none"
      data-testid={testId} />
  </div>
);

export default FuelLanding;
